/**
 * Utility for mapping purity score to rank and effects.
 */

export type PurityRank = "D" | "C" | "B" | "A" | "S" | "S+";

export interface PurityInfo {
  rank: PurityRank;
  color: string;
  glowColor: string;
  hasShine: boolean;
  vfxIntensity: "none" | "low" | "medium" | "high" | "wow";
}

/**
 * Calculates the purity score based on how many talents match the pokemon's types.
 * @param talents List of talent types the pokemon has (e.g., ["FIRE", "WATER"])
 * @param pokemonTypes List of pokemon's own types (e.g., ["FIRE", "FLYING"])
 * @returns Number of matching talents
 */
export const calculatePurityScore = (
  talents: string[] | readonly string[],
  pokemonTypes: string[] | readonly (string | undefined)[],
): number => {
  if (!talents || !pokemonTypes) return 0;

  const validTypes = pokemonTypes.filter((t): t is string => !!t);
  const upperTypes = validTypes
    .filter((t): t is string => !!t)
    .map((t) => t.toUpperCase());
  return talents.filter((talent) => upperTypes.includes(talent.toUpperCase()))
    .length;
};

/**
 * Maps a purity score (number of talents matching type, 0-9) to a rank.
 * D  => 0–2
 * C  => 3–4
 * B  => 5
 * A  => 6–7
 * S  => 8
 * S+ => 9
 */
export const getPurityInfo = (purity: number): PurityInfo => {
  if (purity >= 9) {
    return {
      rank: "S+",
      color: "text-pk-yellow-1",
      glowColor: "rgba(251, 191, 36, 0.8)", // pk-yellow-1 approx
      hasShine: true,
      vfxIntensity: "wow",
    };
  }
  if (purity >= 8) {
    return {
      rank: "S",
      color: "text-pk-yellow-3",
      glowColor: "rgba(250, 175, 31, 0.6)", // pk-yellow-3
      hasShine: true,
      vfxIntensity: "high",
    };
  }
  if (purity >= 6) {
    return {
      rank: "A",
      color: "text-pk-purple-4",
      glowColor: "rgba(153, 114, 166, 0.4)", // pk-purple-4
      hasShine: true,
      vfxIntensity: "medium",
    };
  }
  if (purity >= 5) {
    return {
      rank: "B",
      color: "text-pk-blue-4",
      glowColor: "rgba(114, 137, 180, 0.3)", // pk-blue-4
      hasShine: false,
      vfxIntensity: "low",
    };
  }
  if (purity >= 3) {
    return {
      rank: "C",
      color: "text-pk-gray-4",
      glowColor: "transparent",
      hasShine: false,
      vfxIntensity: "none",
    };
  }
  return {
    rank: "D",
    color: "text-pk-red-4",
    glowColor: "transparent",
    hasShine: false,
    vfxIntensity: "none",
  };
};
